import React, {
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { alpha, styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Toolbar from "@mui/material/Toolbar";
import { LeftMenu } from "@/features";
import { IconButton, Link, SwipeableDrawer, Typography } from "@mui/material";
import { ChevronLeft } from "@mui/icons-material";
import { PartnerImagePath } from "@/hooks";
import { useTextDomainContext } from "@/app/providers";
import { getPartnerKey } from "@/utils";
import { UserInfoBox } from "@/features/layout";

const DrawerHeader = styled("div")(() => ({}));

const DrawerMenu = styled(Box)(() => ({
  display: "flex",
  flex: 1,
}));

const DrawerFooter = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  alignItems: "center",
  padding: theme.spacing(1),
  flexGrow: 0,
  backgroundColor: "transparent",
}));

const LogoImage = styled("img")(() => ({
  // width: "fit-content",
  height: "fit-content",
  maxHeight: "100%",
  maxWidth: "100%",
}));

interface SideDrawerProps {
  drawerWidth: number;
  isDrawerOpen: boolean;
  setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  children?: ReactNode;
}

export const SideDrawer = ({
  drawerWidth,
  isDrawerOpen,
  setIsDrawerOpen,
  children,
}: SideDrawerProps): ReactElement => {
  const partnerKey = getPartnerKey();
  const [logo, setLogo] = useState<string>();
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  useEffect(() => {
    let isMounted = true;

    PartnerImagePath(partnerKey, "logos/logo-light.png").then((imageUrl) => {
      if (isMounted && typeof imageUrl === "string") {
        setLogo(imageUrl);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [partnerKey]);

  return (
    <>
      <Box
        component="nav"
        sx={{
          width: {
            sm: drawerWidth,
          },
          flexShrink: {
            sm: 0,
          },
        }}
      >
        <Drawer
          variant="permanent"
          sx={{
            display: {
              xs: "none",
              sm: "flex",
            },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: "leftMenu.backgroundColorMenu",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "leftMenu.scrollbarThumb",
                borderRadius: "8px",
              },
            },
          }}
          open
        >
          <DrawerHeader>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                padding: "10px 20px",
                height: "75px",
              }}
            >
              {logo && <LogoImage alt="logo" src={logo} />}
            </Box>
          </DrawerHeader>
          <DrawerMenu>
            <LeftMenu />
          </DrawerMenu>
          <DrawerFooter>
            <Box
              sx={{
                maxWidth: "100%",
                width: "185px",
              }}
            >
              <img src="/media/power-stars.png" alt="Stars" height={20} />
            </Box>
            <Box
              sx={{
                fontSize: "1rem",
                color: "#FFFFFF",
              }}
            >
              <Typography component="span" variant="inherit" sx={{}}>
                {gettext("Data Delivered By")}
              </Typography>
              &nbsp;
              <Typography
                component="span"
                variant="inherit"
                sx={{
                  fontWeight: 700,
                }}
              >
                <Link
                  href="https://fabeke.com"
                  target="_blank"
                  sx={{
                    textDecoration: "none",
                    color: "#FFFFFF",
                  }}
                >
                  {gettext("Fabeke")}
                </Link>
              </Typography>
            </Box>
            <Box
              sx={(theme) => ({
                borderRadius: "8px",
                width: "100%",
                display: "flex",
                justifyContent: "flex-start",
                px: 2,
                mt: 2,
                backgroundColor: alpha(
                  theme.palette.leftMenu.backgroundColorSelected,
                  0.28,
                ),
                color: "leftMenu.colorSelected",
              })}
            >
              <UserInfoBox />
            </Box>
          </DrawerFooter>
        </Drawer>

        <SwipeableDrawer
          anchor="left"
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          onOpen={() => setIsDrawerOpen(true)}
          sx={{
            display: {
              xs: "block",
              sm: "none",
            },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: "leftMenu.backgroundColorMenu",
            },
          }}
        >
          <DrawerHeader>
            <Box sx={{}}>
              <Link href="">{logo && <LogoImage alt="logo" src={logo} />}</Link>
            </Box>
            <IconButton onClick={() => setIsDrawerOpen(false)}>
              <ChevronLeft sx={{ color: "white" }} />
            </IconButton>
          </DrawerHeader>
        </SwipeableDrawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: `calc(100% - ${drawerWidth}px)`,
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </>
  );
};
